<script setup lang="ts">
import type { Component } from 'vue'
import { useI18n } from 'vue-i18n'
import IconBlock from '~icons/material-symbols/block'
import IconCheck from '~icons/material-symbols/check'
import IconLock from '~icons/material-symbols/lock-outline'
import IconStar from '~icons/material-symbols/star-outline'
import IconWarning from '~icons/material-symbols/warning-outline'
import { MODEL_CARD_STATES, type ModelCardState } from '~/features/my-wallet/constants/my-wallet'

interface Props {
  id: string
  state: string
}

const props = defineProps<Props>()

const { t } = useI18n()

const knownState = computed<ModelCardState | null>(() =>
  (MODEL_CARD_STATES as readonly string[]).includes(props.state) ? props.state as ModelCardState : null,
)

const STATE_ICON: Record<ModelCardState, Component | null> = {
  ENABLED: IconCheck,
  DEFAULT: null,
  LOCKED: IconLock,
  DISABLED: IconWarning,
  BLOCKED: IconBlock,
  WAIVED: IconStar,
}

const stateLabel = computed(() => knownState.value ? t(`my_wallet.models.chips.${knownState.value}`) : '')
const stateIcon = computed(() => knownState.value ? STATE_ICON[knownState.value] : null)
</script>

<template>
  <span
    v-if="knownState === 'LOCKED'"
    :id="id"
    class="status-chip -locked"
    role="img"
    :aria-label="stateLabel"
  >
    <component :is="stateIcon" class="status-chip-icon -lock" aria-hidden="true" />
  </span>
  <span
    v-else-if="knownState"
    :id="id"
    class="status-chip"
    :class="`-${knownState.toLowerCase()}`"
  >
    <component :is="stateIcon" v-if="stateIcon" class="status-chip-icon" aria-hidden="true" />
    {{ stateLabel }}
  </span>
</template>

<style scoped lang="scss">
.status-chip {
  @apply inline-flex items-center gap-1 h-6 px-3 rounded-full whitespace-nowrap flex-shrink-0;
  @apply fonts-body-small-bold uppercase;

  &.-default,
  &.-locked {
    @apply bg-neutral-high-medium text-neutral-low-medium;
  }

  &.-enabled {
    @apply bg-feedback-positive-light text-feedback-positive-dark;
  }

  &.-disabled {
    @apply bg-feedback-warning-light text-feedback-warning-dark;
  }

  &.-blocked {
    @apply bg-feedback-negative-light text-feedback-negative-dark;
  }

  &.-waived {
    @apply border border-feedback-positive-dark text-feedback-positive-dark;
    @apply bg-brand-secondary-light;
  }

  .status-chip-icon {
    @apply w-3 h-3 flex-shrink-0;

    &.-lock {
      @apply w-4 h-4;
    }
  }
}
</style>
